/**
 * Switch.jsx — The primary conversion landing page for paid ads and
 * SSU-community sharing. This is where a coach who currently uses
 * another software lands, plays with the Coach Savings Calculator,
 * sees real peers already running on SplashPad, and starts a trial.
 *
 * URL: /switch
 *
 * Architectural rules:
 *   • Single-purpose funnel. No global nav, no marketing feature
 *     bento, no legalese in the body. Just: hero → calculator →
 *     social proof → SSU bridge → final CTA. Minimal footer.
 *   • Reuse the exact <LandingProfitCalculator /> from the main
 *     landing page so the number a coach sees on /switch matches
 *     the one they see on /. Consistency = trust.
 *   • Live coach roster (from /api/public/coaches/map) so the
 *     "join us" story shows real humans, not stock photos.
 *   • Every interactive element carries a data-testid for automated
 *     verification. This page is A/B critical.
 *
 * Design tokens (from /app/design_guidelines.md):
 *   Navy #0a1628 · Ocean #1a3a5c · Cyan #00d4ff ·
 *   Coral #ff6b35 · Gold #ffd700 · Light BG #f4f7fb ·
 *   Radius 0.9rem, standard shadow sd-shadow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '@/components/Seo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
    Calculator,
    Zap,
    Wallet,
    ShieldCheck,
    Sparkles,
    Users,
    Globe,
    ArrowRight,
    CheckCircle2,
    Gift,
    Percent,
    HelpCircle,
    Route,
} from 'lucide-react';
import { LandingProfitCalculator } from '@/components/LandingProfitCalculator';
import { HeroVideo } from '@/components/HeroVideo';
import api from '@/lib/api';

// -----------------------------------------------------------------
// SEO / JSON-LD constants for the /switch page.
// -----------------------------------------------------------------
const SEO = {
    title: 'Switch to SplashPad — Coach Savings Calculator',
    description:
        'Compare your current swim software to SplashPad. See exactly how much you\u2019d save every year with just $0.25 per transaction and no percentage fees. Free 30-day trial.',
    canonical: 'https://splashpad.pro/switch',
};

const PAGE_JSONLD = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Switch to SplashPad',
    description: SEO.description,
    url: SEO.canonical,
    isPartOf: {
        '@type': 'WebSite',
        name: 'SplashPad',
        url: 'https://splashpad.pro',
    },
    mainEntity: {
        '@type': 'SoftwareApplication',
        name: 'SplashPad',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            description: '30-day free trial. Cancel anytime.',
        },
    },
};

// -----------------------------------------------------------------
// "How switching works" — three concrete steps. SEO audit flagged
// /switch as thin text (hero + calculator + CTAs only), so this and
// the FAQ below give crawlers real, indexable copy about the actual
// migration process.
// -----------------------------------------------------------------
const SWITCH_STEPS = [
    {
        icon: Sparkles,
        title: 'Start your trial and pick a plan',
        body:
            'Create your coach account in about two minutes. Your 30-day trial includes every Pro feature so you can set things up exactly the way you want before you pay anything.',
    },
    {
        icon: Users,
        title: 'Bring your families over',
        body:
            'Add your existing packages and class times, then share your new branded page link. Parents register themselves, sign the digital waiver, and pay in one flow \u2014 no re-keying spreadsheets on your end.',
    },
    {
        icon: Wallet,
        title: 'Get paid directly. Just 25\u00a2 per transaction.',
        body:
            'Connect your Stripe account and every lesson payment lands in your bank, not ours. SplashPad never takes a percentage of your revenue \u2014 just a flat $0.25 per successful transaction on top of your monthly subscription. That\u2019s it.',
    },
];

// -----------------------------------------------------------------
// FAQ — rendered as an accordion AND emitted as FAQPage JSON-LD so
// Google can show rich results. Keep questions phrased the way a
// coach actually types them into a search box.
// -----------------------------------------------------------------
const SWITCH_FAQ = [
    {
        q: 'How long does it take to switch my swim school to SplashPad?',
        a: 'Most independent instructors are fully live within a day. You create your account, add your packages and schedule, and share your branded page link with parents. Coaches on the Pro plan also get a 1-on-1 onboarding call to walk through the setup together.',
    },
    {
        q: 'Do I have to re-enter all of my students and families?',
        a: 'No. Parents create their own accounts, add their swimmers, and sign your waiver the first time they register on your SplashPad page. Everything they enter is saved for future sessions, so you never maintain a spreadsheet again.',
    },
    {
        q: 'What does SplashPad cost compared to my current software?',
        a: 'SplashPad charges a flat monthly subscription plus just $0.25 per successful transaction \u2014 never a percentage. Many swim-school platforms add a per-registration or percentage fee on top of Stripe processing. Use the calculator on this page to compare your current total cost with SplashPad using your own lesson volume.',
    },
    {
        q: 'Can I run a free trial before I cancel my current software?',
        a: 'Yes. Your 30-day trial includes every Pro feature and does not require you to cancel anything else. Run both side by side, and only switch families over once you are confident SplashPad works for your program.',
    },
    {
        q: 'How do I get paid for lessons on SplashPad?',
        a: 'Payments run through Stripe Connect. Parents pay by card on your registration page and the money is deposited directly into your own bank account on Stripe\u2019s standard payout schedule. SplashPad never holds your funds.',
    },
    {
        q: 'What is the SSU member discount and how do I use it?',
        a: 'Members of the Superhero Swim University (SSU) community receive the Starter plan free and the Pro plan at 50% off for as long as their membership is active. The promo code is shared inside the SSU Skool community and is entered during signup.',
    },
    {
        q: 'Can I cancel if SplashPad is not the right fit?',
        a: 'Yes. There are no contracts. You can cancel from your account settings at any time and your subscription simply ends at the close of the current billing period.',
    },
];

const FAQ_JSONLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: SWITCH_FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
    })),
};

// -----------------------------------------------------------------
// Trust chips — displayed just below the hero H1. Keep the copy
// short and factual; no marketing fluff.
// -----------------------------------------------------------------
const TRUST_CHIPS = [
    { icon: Percent, label: 'Just 25\u00a2 per transaction' },
    { icon: Sparkles, label: 'Free 30-day trial' },
    { icon: ShieldCheck, label: 'Cancel anytime' },
    { icon: Users, label: 'Coach-built' },
];

// -----------------------------------------------------------------
// <SwitchCoachRoster />
// Live social proof — pulls the active coach directory from the same
// endpoint the map on the main landing page uses. Shows:
//   • Total live coach count (from `page_active=True` + valid geo)
//   • A compact 12-card grid of representative coaches
//   • A link out to the full map for anyone who wants to explore
// -----------------------------------------------------------------
function CoachAvatar({ coach }) {
    const initials = (() => {
        const source = coach.display_name || `${coach.first_name || ''} ${coach.last_name || ''}`;
        const parts = source.trim().split(/\s+/).slice(0, 2);
        return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
    })();
    const bg = coach.primary_color || '#0a1628';
    const cityLine = coach.geocoded_city
        ? `${coach.geocoded_city}${coach.geocoded_state ? `, ${coach.geocoded_state}` : ''}`
        : coach.service_area || 'On SplashPad';

    return (
        <Link
            to={`/coach/${coach.slug || ''}`}
            className="sd-focus group flex items-center gap-3 rounded-2xl border border-[#0a1628]/10 bg-white p-3 shadow-[0_6px_14px_rgba(10,22,40,0.06)] ring-1 ring-transparent transition-[transform,box-shadow,border-color,ring-color] hover:-translate-y-0.5 hover:border-[#00d4ff]/40 hover:shadow-[0_10px_25px_rgba(10,22,40,0.10)] hover:ring-[#00d4ff]/30"
            data-testid={`switch-coach-card-${coach.slug || coach.id}`}
        >
            {coach.logo_url ? (
                <img
                    src={coach.logo_url}
                    alt={coach.display_name || 'Coach'}
                    className="h-12 w-12 shrink-0 rounded-xl object-cover"
                    loading="lazy"
                />
            ) : (
                <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-display text-base font-bold text-white"
                    style={{ backgroundColor: bg }}
                    aria-hidden="true"
                >
                    {initials}
                </div>
            )}
            <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-[#0a1628]">{coach.display_name || `${coach.first_name || 'Coach'} ${coach.last_name || ''}`.trim()}</div>
                <div className="truncate text-xs text-[#1a3a5c]/70">{cityLine}</div>
            </div>
        </Link>
    );
}

// -----------------------------------------------------------------
// Demo / test-account heuristic filter. Public-map coaches include
// admin-created test accounts ("Coach Sunset Demo", "WL Test",
// "Chicken Sandwich", "Testc1778367787", …) which look unprofessional
// on a paid-ad conversion page. We hide any coach whose display name
// matches obvious test/demo tokens.
//
// TODO(long-term): replace with a backend `is_marketing_showcase` flag
// on `instructor_profiles` so admins can curate the marketing surface
// explicitly. Until then, this frontend filter is a pragmatic firewall.
// -----------------------------------------------------------------
const TEST_COACH_TOKEN_RE = /\b(test|demo|preview|prev|sample|dummy|placeholder|tempuser|qa|staging|wl)\b|\d{4,}|chicken\s*sandwich/i;

function looksLikeTestAccount(coach) {
    const name = `${coach.display_name || ''} ${coach.first_name || ''} ${coach.last_name || ''}`.trim();
    if (!name) return true;
    if (TEST_COACH_TOKEN_RE.test(name)) return true;
    // Slug can also carry test tokens even if the display name got cleaned up.
    if (coach.slug && TEST_COACH_TOKEN_RE.test(coach.slug)) return true;
    return false;
}

function SwitchCoachRoster() {
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get('/public/coaches/map')
            .then((r) => {
                if (cancelled) return;
                const raw = Array.isArray(r.data) ? r.data : [];
                setCoaches(raw.filter((c) => !looksLikeTestAccount(c)));
            })
            .catch(() => { if (!cancelled) setErrored(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // Pick a stable-ish first 12 for the marketing wall. The endpoint
    // returns coaches in a fixed order (Mongo natural order) so this
    // is stable across page refreshes within the cache window.
    const previewCoaches = useMemo(() => coaches.slice(0, 12), [coaches]);
    const totalCount = coaches.length;

    if (loading) {
        return (
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="switch-coach-roster-skeleton">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-2xl bg-white/70" />
                ))}
            </div>
        );
    }
    if (errored || totalCount === 0) {
        // Graceful empty state — used when the API errors OR when every
        // returned coach was filtered out as a test account (e.g., on
        // the preview environment). Turns the void into a value prop.
        return (
            <div className="rounded-2xl border border-[#0a1628]/10 bg-white p-8 text-center" data-testid="switch-coach-roster-empty">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#00d4ff]/15 text-[#00d4ff]">
                    <Users className="h-6 w-6" />
                </div>
                <div className="text-lg font-semibold text-[#0a1628]">The SplashPad coach community is growing every week.</div>
                <p className="mx-auto mt-2 max-w-md text-sm text-[#1a3a5c]">
                    New coaches join from around the world &mdash; you could be next. Start your free trial and get your branded coach page in under 10 minutes.
                </p>
                <Link
                    to="/#find-coach"
                    className="sd-focus mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0a1628] underline underline-offset-2 hover:text-[#00d4ff]"
                >
                    Explore the coach directory <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </div>
        );
    }
    return (
        <div data-testid="switch-coach-roster">
            <div className="mb-5 flex flex-wrap items-baseline justify-center gap-x-2 text-center">
                <span className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl" data-testid="switch-coach-count">
                    {totalCount}
                </span>
                <span className="text-base text-[#1a3a5c]">
                    {totalCount === 1 ? 'coach is' : 'coaches are'} already running their swim school on SplashPad.
                </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {previewCoaches.map((c) => (
                    <CoachAvatar key={c.id || c.slug} coach={c} />
                ))}
            </div>
            {totalCount > previewCoaches.length && (
                <div className="mt-5 text-center">
                    <Link
                        to="/#find-coach"
                        className="sd-focus inline-flex items-center gap-1 text-sm font-semibold text-[#0a1628] underline underline-offset-2 hover:text-[#00d4ff]"
                        data-testid="switch-see-all-coaches-link"
                    >
                        See all {totalCount} coaches on the map <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------
// Page
// -----------------------------------------------------------------
const Switch = () => {
    // Fade-in animations tuned to feel snappy on an ad landing page —
    // no long transitions that make the page feel sluggish.
    const fadeUp = {
        initial: { opacity: 0, y: 16 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: { duration: 0.4, ease: 'easeOut' },
    };

    return (
        <div className="min-h-screen bg-white text-[#0a1628]">
            <Seo
                title={SEO.title}
                description={SEO.description}
                canonical={SEO.canonical}
                jsonLd={[PAGE_JSONLD, FAQ_JSONLD]}
            />

            {/* ============================================================ */}
            {/* Minimal sticky header — logo + a low-emphasis "Log in".      */}
            {/* No nav menu; this page is a conversion funnel, not a hub.   */}
            {/* ============================================================ */}
            <header className="sticky top-0 z-40 border-b border-[#0a1628]/[0.04] bg-white/85 shadow-[0_1px_0_rgba(10,22,40,0.04)] backdrop-blur-md" data-testid="switch-header">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
                    <Link to="/" className="sd-focus flex items-center gap-2" data-testid="switch-logo-home">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0a1628] font-display text-lg font-bold text-white">S</div>
                        <div className="leading-tight">
                            <div className="font-display text-base font-bold text-[#0a1628]">SplashPad</div>
                            <div className="text-[10px] font-medium uppercase tracking-widest text-[#1a3a5c]/70">by SSU</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1 sm:gap-2">
                        <Link
                            to="/login"
                            className="sd-focus rounded-lg px-3 py-1.5 text-sm text-[#1a3a5c]/70 hover:text-[#0a1628]"
                            data-testid="switch-header-login"
                        >
                            Log in
                        </Link>
                        <Link to="/signup?role=instructor">
                            <Button className="sd-cta sd-focus h-9 rounded-xl text-sm font-semibold" data-testid="switch-header-cta">
                                Start free trial
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* ============================================================ */}
            {/* HERO                                                         */}
            {/* Navy background with a subtle radial cyan glow. This is the */}
            {/* first thing an ad-clicker sees — must communicate value in  */}
            {/* under 2 seconds.                                            */}
            {/* ============================================================ */}
            <section
                data-testid="switch-hero"
                className="sd-noise relative overflow-hidden bg-gradient-to-b from-[#0a1628] to-[#0e2038] pb-16 pt-14 text-white lg:pb-28 lg:pt-24"
            >
                {/* Subtle radial glow accent (kept under 20% viewport per guidelines). */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
                    style={{ background: 'radial-gradient(closest-side, rgba(0,212,255,0.35), transparent 70%)' }}
                />
                <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
                    <motion.div {...fadeUp}>
                        <Badge className="mb-4 border-[#00d4ff]/40 bg-[#00d4ff]/15 text-[#00d4ff] hover:bg-[#00d4ff]/15" data-testid="switch-hero-kicker">
                            <Calculator className="mr-1 h-3 w-3" /> Coach Savings Calculator
                        </Badge>
                        <h1 className="font-display text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="switch-hero-headline">
                            See exactly what you&rsquo;d save on SplashPad.
                        </h1>
                        <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/80 sm:text-base lg:text-lg" data-testid="switch-hero-sub">
                            Every dollar you earn should be yours to keep. Compare your current swim software to SplashPad &mdash; no signup, no sales call, just numbers.
                        </p>

                        {/* Hero promotional video. Autoplays muted + looped so
                            the mascot narrative starts telling itself before
                            the visitor even scrolls. Users can tap for sound
                            or pause via the controls. Prefers-reduced-motion
                            visitors see the poster + a play button instead. */}
                        <div className="mx-auto mt-8 max-w-3xl" data-testid="switch-hero-video-wrap">
                            <HeroVideo dataTestId="switch-hero-video" />
                        </div>

                        {/* Trust chip strip */}
                        <div className="mt-7 flex flex-wrap justify-center gap-2" data-testid="switch-hero-chips">
                            {TRUST_CHIPS.map(({ icon: Icon, label }) => (
                                <span
                                    key={label}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur"
                                >
                                    <Icon className="h-3.5 w-3.5 text-[#00d4ff]" /> {label}
                                </span>
                            ))}
                        </div>

                        {/* Hero CTAs — primary is "Run my numbers" (scroll to
                            calculator) because ad-clickers arrive with intent
                            to compare, not to sign up cold. Secondary is a
                            low-emphasis "Start free trial" text link so the
                            zero-friction path stays visible. */}
                        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row" data-testid="switch-hero-ctas">
                            <a
                                href="#switch-calculator"
                                className="sd-focus inline-flex h-12 items-center justify-center rounded-xl bg-white/10 px-6 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/15"
                                data-testid="switch-hero-scroll-cta"
                            >
                                Run my numbers <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                            <Link
                                to="/signup?role=instructor"
                                className="sd-focus text-sm font-medium text-white/80 underline underline-offset-4 hover:text-white"
                                data-testid="switch-hero-trial-link"
                            >
                                Or start my free trial &rarr;
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/* CALCULATOR                                                   */}
            {/* Reused component. Overlaps the hero visually via -mt-10 so  */}
            {/* the transition feels like the calculator is emerging out    */}
            {/* of the hero band. The vertical gradient fade behind the     */}
            {/* calculator hides the hard seam between the navy hero and    */}
            {/* the white body so the emergence feels natural.              */}
            {/* ============================================================ */}
            <div className="relative -mt-10 lg:-mt-16">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0e2038] to-transparent opacity-0"
                />
                <LandingProfitCalculator defaultCompetitorId={null} anchorId="switch-calculator" hideHeader />
            </div>

            {/* ============================================================ */}
            {/* SOCIAL PROOF — LIVE COACH ROSTER                             */}
            {/* ============================================================ */}
            <section className="bg-[#f4f7fb] py-16 lg:py-20" data-testid="switch-social-proof-section">
                <div className="mx-auto max-w-5xl px-4 sm:px-6">
                    <motion.div {...fadeUp} className="mx-auto mb-10 max-w-2xl text-center">
                        <Badge className="mb-3 bg-[#0a1628] text-white hover:bg-[#0a1628]" data-testid="switch-social-kicker">
                            <Globe className="mr-1 h-3 w-3" /> Real coaches, right now
                        </Badge>
                        <h2 className="font-display text-3xl font-bold text-[#0a1628] sm:text-4xl" data-testid="switch-social-heading">
                            You&rsquo;ll be in great company.
                        </h2>
                        <p className="mt-3 text-sm text-[#1a3a5c] sm:text-base">
                            Every coach below runs their real swim school on SplashPad today. Click any card to see their branded page &mdash; the same kind of page you&rsquo;ll get on day one.
                        </p>
                    </motion.div>
                    <Separator className="mx-auto mb-8 max-w-md bg-[#0a1628]/10" data-testid="switch-social-separator" />
                    <motion.div {...fadeUp}>
                        <SwitchCoachRoster />
                    </motion.div>
                    <motion.div {...fadeUp} className="mt-10 text-center">
                        <Link to="/signup?role=instructor">
                            <Button className="sd-cta sd-focus h-12 max-w-full rounded-xl px-4 text-sm font-bold sm:px-6 sm:text-base" data-testid="switch-social-cta">
                                <span className="inline whitespace-normal">Join them &mdash; start my free 30-day trial</span>
                                <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                            </Button>
                        </Link>
                        <p className="mt-2 text-xs text-[#1a3a5c]/70">Cancel anytime. Pro includes a 1-on-1 onboarding call.</p>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/* SSU COMMUNITY BRIDGE                                         */}
            {/* Matched green/red discount cards that mirror the pricing    */}
            {/* section on the main landing page. Both cards deep-link to   */}
            {/* the same Skool community sign-up URL where members receive  */}
            {/* the promo code.                                             */}
            {/* ============================================================ */}
            <section className="bg-white py-16 lg:py-20" data-testid="switch-ssu-section">
                <div className="mx-auto max-w-5xl px-4 sm:px-6">
                    <motion.div {...fadeUp} className="mx-auto mb-10 max-w-2xl text-center">
                        <Badge className="mb-3 border-[#ffd700]/60 bg-[#ffd70022] text-[#8a6d00] hover:bg-[#ffd70033]" data-testid="switch-ssu-kicker">
                            <Gift className="mr-1 h-3 w-3" /> For SSU members
                        </Badge>
                        <h2 className="font-display text-3xl font-bold text-[#0a1628] sm:text-4xl" data-testid="switch-ssu-heading">
                            Already in the SSU community?
                        </h2>
                        <p className="mt-3 text-[#1a3a5c]">
                            Superhero Swim University members get SplashPad at a permanent discount &mdash; Starter is free and Pro is 50% off, forever.
                        </p>
                    </motion.div>
                    <motion.div {...fadeUp} className="grid gap-6 md:grid-cols-2">
                        {/* Starter — green accent bar */}
                        <Card className="relative flex flex-col overflow-hidden border border-[#0a1628]/10 bg-white p-6 shadow-[0_10px_25px_rgba(10,22,40,0.06)]" data-testid="switch-ssu-starter-card">
                            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-[#19c37d]" />
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#19c37d]/10 text-[#19c37d]">
                                    <CheckCircle2 className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-[#19c37d]">Starter plan</div>
                                    <div className="font-display text-xl font-bold text-[#0a1628]">FREE for SSU members</div>
                                </div>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-[#1a3a5c]">
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#19c37d]" /> Branded coach page &amp; parent portal</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#19c37d]" /> Digital waivers, packages, class scheduling</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#19c37d]" /> Direct Stripe Connect payouts</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#19c37d]" /> Up to 40 active students</li>
                            </ul>
                            <div className="mt-auto pt-6">
                                <Button
                                    asChild
                                    className="sd-focus h-11 w-full rounded-xl bg-[#19c37d] font-semibold text-white shadow-sm hover:bg-[#149a62]"
                                    data-testid="switch-ssu-starter-cta-wrap"
                                >
                                    <a
                                        href="https://www.skool.com/superheroswimuniversity/about?ref=2eba9baa063e4d71916d29f262f0acd2"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="switch-ssu-starter-cta"
                                    >
                                        Join the SSU community <ArrowRight className="ml-2 h-4 w-4" />
                                    </a>
                                </Button>
                                <p className="mt-2 text-center text-[11px] text-[#1a3a5c]/70">Requires active SSU membership</p>
                            </div>
                        </Card>

                        {/* Pro — red accent bar */}
                        <Card className="relative flex flex-col overflow-hidden border border-[#0a1628]/10 bg-white p-6 shadow-[0_10px_25px_rgba(10,22,40,0.06)]" data-testid="switch-ssu-pro-card">
                            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-[#dc2626]" />
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#dc2626]/10 text-[#dc2626]">
                                    <Zap className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-[#dc2626]">Pro plan</div>
                                    <div className="font-display text-xl font-bold text-[#0a1628]">50% OFF for SSU members</div>
                                </div>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-[#1a3a5c]">
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" /> Everything in Starter</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" /> Unlimited students &amp; drag-and-drop landing page</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" /> Multi-coach staff portal &amp; broadcast tools</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" /> <strong className="text-[#0a1628]">$39.99/mo</strong> while you&rsquo;re an SSU member</li>
                            </ul>
                            <div className="mt-auto pt-6">
                                <Button
                                    asChild
                                    className="sd-focus h-11 w-full rounded-xl bg-[#dc2626] font-semibold text-white shadow-sm hover:bg-[#b91c1c]"
                                    data-testid="switch-ssu-pro-cta-wrap"
                                >
                                    <a
                                        href="https://www.skool.com/superheroswimuniversity/about?ref=2eba9baa063e4d71916d29f262f0acd2"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="switch-ssu-pro-cta"
                                    >
                                        Join the SSU community <ArrowRight className="ml-2 h-4 w-4" />
                                    </a>
                                </Button>
                                <p className="mt-2 text-center text-[11px] text-[#1a3a5c]/70">Requires active SSU membership</p>
                            </div>
                        </Card>
                    </motion.div>
                    <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-[#1a3a5c]/70">
                        Codes are community-distributed inside Skool for verification. Apply your code at signup &mdash; discounts apply as long as you&rsquo;re an active SSU member.
                    </p>
                </div>
            </section>

            {/* ============================================================ */}
            {/* HOW SWITCHING WORKS                                          */}
            {/* SEO audit fix — /switch was flagged as "thin text". These   */}
            {/* three steps describe the actual migration path in plain    */}
            {/* language so the page has real, indexable copy.             */}
            {/* ============================================================ */}
            <section className="bg-[#f4f7fb] py-16 lg:py-20" data-testid="switch-how-section">
                <div className="mx-auto max-w-5xl px-4 sm:px-6">
                    <motion.div {...fadeUp} className="mx-auto mb-10 max-w-2xl text-center">
                        <Badge className="mb-3 bg-[#0a1628] text-white hover:bg-[#0a1628]" data-testid="switch-how-kicker">
                            <Route className="mr-1 h-3 w-3" /> How switching works
                        </Badge>
                        <h2 className="font-display text-3xl font-bold text-[#0a1628] sm:text-4xl" data-testid="switch-how-heading">
                            Three steps. No downtime. No spreadsheets.
                        </h2>
                        <p className="mt-3 text-sm text-[#1a3a5c] sm:text-base" data-testid="switch-how-intro">
                            Moving a swim school between platforms usually means exporting rosters, chasing signatures, and re-explaining
                            payment links to every family. SplashPad is built so parents do the data entry once &mdash; on your branded page
                            &mdash; and you simply run lessons.
                        </p>
                    </motion.div>
                    <motion.div {...fadeUp} className="grid gap-5 md:grid-cols-3">
                        {SWITCH_STEPS.map(({ icon: Icon, title, body }, idx) => (
                            <Card
                                key={title}
                                className="relative flex flex-col border border-[#0a1628]/10 bg-white p-6 shadow-[0_10px_25px_rgba(10,22,40,0.06)]"
                                data-testid={`switch-how-step-${idx + 1}`}
                            >
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00d4ff]/15 text-[#0a1628]">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <span className="font-display text-sm font-bold uppercase tracking-widest text-[#1a3a5c]/60">Step {idx + 1}</span>
                                </div>
                                <h3 className="font-display text-lg font-bold text-[#0a1628]">{title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#1a3a5c]">{body}</p>
                            </Card>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/* FAQ                                                          */}
            {/* Mirrors FAQ_JSONLD exactly so the visible copy and the      */}
            {/* structured data never drift apart (a Google rich-result     */}
            {/* eligibility requirement).                                   */}
            {/* ============================================================ */}
            <section className="bg-white py-16 lg:py-20" data-testid="switch-faq-section">
                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <motion.div {...fadeUp} className="mx-auto mb-8 max-w-2xl text-center">
                        <Badge className="mb-3 border-[#00d4ff]/40 bg-[#00d4ff]/15 text-[#0a1628] hover:bg-[#00d4ff]/15" data-testid="switch-faq-kicker">
                            <HelpCircle className="mr-1 h-3 w-3" /> Questions coaches ask before switching
                        </Badge>
                        <h2 className="font-display text-3xl font-bold text-[#0a1628] sm:text-4xl" data-testid="switch-faq-heading">
                            Switching to SplashPad, answered.
                        </h2>
                    </motion.div>
                    <motion.div {...fadeUp}>
                        <Accordion type="single" collapsible className="w-full" data-testid="switch-faq-accordion">
                            {SWITCH_FAQ.map(({ q, a }, idx) => (
                                <AccordionItem key={q} value={`faq-${idx}`} className="border-[#0a1628]/10" data-testid={`switch-faq-item-${idx + 1}`}>
                                    <AccordionTrigger
                                        className="sd-focus text-left text-base font-semibold text-[#0a1628] hover:text-[#1a3a5c] hover:no-underline"
                                        data-testid={`switch-faq-trigger-${idx + 1}`}
                                    >
                                        {q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm leading-relaxed text-[#1a3a5c]" data-testid={`switch-faq-answer-${idx + 1}`}>
                                        {a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/* FINAL CTA BANNER                                             */}
            {/* Navy full-width band with a coral CTA button — the coral    */}
            {/* pops against navy for a clear "one thing to do" signal.     */}
            {/* ============================================================ */}
            <section className="sd-noise bg-gradient-to-br from-[#0a1628] via-[#0a1628] to-[#0e2038] py-16 text-white lg:py-24" data-testid="switch-final-cta-section">
                <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
                    <motion.div {...fadeUp}>
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur">
                            <Wallet className="h-3.5 w-3.5 text-[#00d4ff]" /> Ready to keep every dollar?
                        </div>
                        <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl" data-testid="switch-final-headline">
                            Your free 30-day trial starts in one click.
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-sm text-white/80 sm:text-base" data-testid="switch-final-sub">
                            No percentage fees \u2014 just 25\u00a2 per transaction. Your swim school, running the way it should.
                        </p>
                        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                            <Link to="/signup?role=instructor" className="w-full sm:w-auto">
                                <Button className="sd-focus h-14 w-full rounded-xl bg-[#ff6b35] px-8 text-base font-bold text-white shadow-lg ring-2 ring-[#ff6b35]/30 hover:bg-[#ff5722] sm:w-auto" data-testid="switch-final-primary-cta">
                                    Start my free 30-day trial <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <a
                                href="#switch-calculator"
                                className="sd-focus text-sm font-medium text-white/80 underline underline-offset-4 hover:text-white"
                                data-testid="switch-final-recalc-link"
                            >
                                Recalculate my savings
                            </a>
                        </div>
                        {/* Reassurance row — three tiny risk-reversal beats
                            using cyan icons so they read as reassurance, not
                            fine print. Kept small (text-xs, white/70) so the
                            coral CTA remains the only saturated element. */}
                        <div
                            className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-white/70"
                            data-testid="switch-final-reassurance-row"
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-[#00d4ff]" /> Free 30-day trial
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-[#00d4ff]" /> Cancel anytime
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Users className="h-4 w-4 text-[#00d4ff]" /> 1-on-1 onboarding (Pro)
                            </span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/* Minimal footer                                               */}
            {/* ============================================================ */}
            <footer className="border-t border-[#0a1628]/10 bg-white py-8" data-testid="switch-footer">
                <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-4 text-center text-xs text-[#1a3a5c]/70 sm:flex-row sm:justify-between sm:px-6">
                    <div>
                        &copy; {new Date().getFullYear()} SplashPad by SSU. Made by a working swim instructor.
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/privacy" className="hover:text-[#0a1628]" data-testid="switch-footer-privacy">Privacy</Link>
                        <Link to="/terms" className="hover:text-[#0a1628]" data-testid="switch-footer-terms">Terms</Link>
                        <Link to="/" className="hover:text-[#0a1628]" data-testid="switch-footer-home">SplashPad.pro</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Switch;
